/**
 * Consolidated WebSocket Manager
 * Single WebSocket client per session with service registry and message routing
 */

import { AppDispatch } from '@/store/index';
import { WebSocketOptions, ConnectionState } from './types';
import { WebSocketMessageRouter } from './messageRouter';
import { WebSocketServiceRegistry } from './serviceRegistry';

// Import existing WebSocket service for compatibility
import websocketService from '../websocket';
import { authService } from '../authService';

export class ConsolidatedWebSocketManager {
  private static instance: ConsolidatedWebSocketManager;
  private messageRouter: WebSocketMessageRouter;
  private serviceRegistry: WebSocketServiceRegistry;
  private options: WebSocketOptions;
  private connectionState: ConnectionState;
  private initialized = false;
  /**
   * Message types already forwarded from the base service to the router.
   * `setupMessageRouting()` runs more than once (constructor, then again after the
   * services are initialized, and again whenever a service is registered late), and
   * `websocketService.on()` appends to a Set of listeners — so without this guard
   * every type wired twice delivered every message twice.
   */
  private wiredMessageTypes: Set<string> = new Set();
  /** One-shot guard for the ping / reconnect / connection-status handlers. */
  private infrastructureHandlersWired = false;
  
  // Private constructor for singleton
  private constructor() {
    this.messageRouter = new WebSocketMessageRouter();
    this.serviceRegistry = new WebSocketServiceRegistry();
    
    this.options = {
      autoReconnect: true,
      reconnectInterval: 10000, // Increased from 3s to 10s
      maxReconnectAttempts: 3,   // Reduced from 5 to 3
      enableMessageDeduplication: true,
      messageTimeout: 5000
    };
    
    this.connectionState = {
      status: 'disconnected',
      reconnectAttempts: 0,
      isAuthenticated: false
    };
    
    // Set up message router to receive messages from base WebSocket service
    this.setupMessageRouting();
    
    console.log('🔧 ConsolidatedWebSocketManager - Initialized');
  }
  
  // Singleton getter
  public static getInstance(): ConsolidatedWebSocketManager {
    if (!ConsolidatedWebSocketManager.instance) {
      ConsolidatedWebSocketManager.instance = new ConsolidatedWebSocketManager();
    }
    return ConsolidatedWebSocketManager.instance;
  }
  
  // Initialize with Redux dispatch
  async initialize(dispatch: AppDispatch, options?: WebSocketOptions): Promise<void> {
    if (this.initialized) {
      console.log('⚠️ ConsolidatedWebSocketManager - Already initialized, skipping');
      return;
    }
    
    
    // Merge options
    this.options = { ...this.options, ...options };
    
    // Set message deduplication settings
    if (this.options.enableMessageDeduplication) {
      this.messageRouter.setMessageTimeout(this.options.messageTimeout || 5000);
    }
    
    // Initialize services first (this registers the message handlers)
    await this.serviceRegistry.initializeServices(this.messageRouter, dispatch);
    
    // Set up message routing AFTER services are initialized
    this.setupMessageRouting();
    
    this.initialized = true;
    
    console.log('✅ ConsolidatedWebSocketManager - Initialization complete with message routing');
  }
  
  // Connect to WebSocket
  async connect(): Promise<void> {
    if (!this.initialized) {
      throw new Error('ConsolidatedWebSocketManager must be initialized before connecting');
    }
    
    if (this.connectionState.status === 'connected') {
      console.log('🔗 ConsolidatedWebSocketManager - Already connected');
      return;
    }
    
    console.log('🔗 ConsolidatedWebSocketManager - Connecting...');
    
    this.updateConnectionState({ status: 'connecting' });
    
    try {
      // Ensure we have the latest token from authService before connecting
      this.syncTokenBeforeConnection();
      
      // Connect using existing WebSocket service
      await websocketService.connect();
      
      this.updateConnectionState({ 
        status: 'connected', 
        lastConnected: new Date(),
        reconnectAttempts: 0,
        isAuthenticated: true
      });
      
      console.log('✅ ConsolidatedWebSocketManager - Connected successfully');
    } catch (error) {
      console.error('❌ ConsolidatedWebSocketManager - Connection failed:', error);
      this.updateConnectionState({ status: 'error' });
      throw error;
    }
  }
  
  // Disconnect from WebSocket
  disconnect(): void {
    console.log('🔌 ConsolidatedWebSocketManager - Disconnecting...');
    
    websocketService.disconnect();
    
    this.updateConnectionState({ 
      status: 'disconnected',
      reconnectAttempts: 0,
      isAuthenticated: false
    });
    
    console.log('✅ ConsolidatedWebSocketManager - Disconnected');
  }
  
  // Currently subscribed farm ID (for auto-unsubscribe on change)
  private subscribedFarmId: string | null = null;

  // Send message through WebSocket
  sendMessage(type: string, data?: any): void {
    if (this.connectionState.status !== 'connected') {
      console.warn('⚠️ ConsolidatedWebSocketManager - Cannot send message, not connected');
      return;
    }

    websocketService.send(type, data);
  }

  // Subscribe to a specific farm's updates
  subscribeFarm(farmId: string): void {
    if (this.subscribedFarmId === farmId) return;

    // Unsubscribe from previous farm
    if (this.subscribedFarmId) {
      this.sendMessage('unsubscribe_farm', { farm_id: this.subscribedFarmId });
    }

    this.subscribedFarmId = farmId;
    this.sendMessage('subscribe_farm', { farm_id: farmId });
    console.log(`📡 ConsolidatedWebSocketManager - Subscribed to farm ${farmId}`);
  }

  // Unsubscribe from farm updates (receive all)
  unsubscribeFarm(): void {
    if (this.subscribedFarmId) {
      this.sendMessage('unsubscribe_farm', { farm_id: this.subscribedFarmId });
      this.subscribedFarmId = null;
      console.log('📡 ConsolidatedWebSocketManager - Unsubscribed from farm, receiving all');
    }
  }

  // Get currently subscribed farm
  getSubscribedFarmId(): string | null {
    return this.subscribedFarmId;
  }
  
  // Get connection status
  getConnectionState(): string {
    return this.connectionState.status;
  }
  
  // Check if connected
  isConnected(): boolean {
    return this.connectionState.status === 'connected';
  }
  
  // Register a service
  async registerService(service: any): Promise<void> {
    console.log(`🔧 ConsolidatedWebSocketManager - Registering service: ${service.name}`);
    
    // Register with service registry (this adds the service but doesn't initialize it yet)
    await this.serviceRegistry.register(service);

    // A service registered AFTER initialize() would otherwise declare message
    // types nobody forwards, and its handlers would never fire. Wiring is
    // idempotent per type, so re-running it only picks up the new ones.
    if (this.initialized) {
      this.setupMessageRouting();
    }

    console.log(`✅ ConsolidatedWebSocketManager - Service registered: ${service.name}`);
  }
  
  // Unregister a service
  async unregisterService(serviceName: string): Promise<void> {
    await this.serviceRegistry.unregister(serviceName);
  }
  
  // Get service statistics
  getStatistics() {
    const routerStats = this.messageRouter.getStatistics();
    const registryStats = this.serviceRegistry.getStatistics();
    
    return {
      connection: this.connectionState,
      router: routerStats,
      services: registryStats,
      initialized: this.initialized
    };
  }
  
  // Cleanup resources
  async cleanup(): Promise<void> {
    console.log('🧹 ConsolidatedWebSocketManager - Starting cleanup...');
    
    // Disconnect WebSocket first
    this.disconnect();
    
    // Cleanup services (this removes their message handlers)
    await this.serviceRegistry.cleanupServices();
    
    // Clear message router (now safe since services are cleaned up)
    this.messageRouter.clearHandlers();
    
    // Reset state. NOTE: `wiredMessageTypes` / `infrastructureHandlersWired` are
    // deliberately NOT reset — cleanup removes the ROUTER's handlers, not the
    // listeners this manager holds on the base websocketService. Those survive, so
    // clearing the guards here would double-wire every type on the next initialize.
    this.initialized = false;

    console.log('✅ ConsolidatedWebSocketManager - Cleanup complete');
  }
  
  // Set up message routing from base WebSocket service
  private setupMessageRouting(): void {
    console.log('🔧 ConsolidatedWebSocketManager - Setting up message routing...');
    
    // The base service emits each inbound message under its OWN `type` and does
    // nothing for a type with no listener (websocket.ts `emit`), so a type absent
    // from this list is dropped SILENTLY — no error, no warning, the feature just
    // looks like it never updates. Hard-coding the list meant every new service
    // had to remember to edit this file too, and two forgot: the whole `opta_*`
    // family and `alert_escalation` were being discarded. So derive it from what
    // the services actually declare. Safe ordering-wise: `initialize()` registers
    // and initializes all services BEFORE calling this.
    const serviceTypes = this.serviceRegistry
      .getServices()
      .flatMap(service => service.getMessageTypes());

    // Infrastructure types no service declares (handled by the manager itself or
    // by hooks listening on the router).
    const INFRASTRUCTURE_TYPES = ['connection_status', 'task_progress'];

    const messageTypes = Array.from(new Set([...serviceTypes, ...INFRASTRUCTURE_TYPES]));
    console.log(
      `🔧 ConsolidatedWebSocketManager - Forwarding ${messageTypes.length} message types:`,
      messageTypes.join(', '),
    );

    messageTypes.forEach(messageType => {
      if (this.wiredMessageTypes.has(messageType)) return;  // never wire twice
      this.wiredMessageTypes.add(messageType);
      websocketService.on(messageType, (message) => {
        // Route through message router
        this.messageRouter.routeMessage({
          type: messageType,
          data: message.data,
          timestamp: message.timestamp || Date.now()
        });
      });
    });

    // The handlers below must exist exactly once each: a second `ping` listener
    // answers every server ping with a second pong, and a second `ws_reconnected`
    // listener fires a duplicate resubscribe + resync storm on every reconnect.
    if (this.infrastructureHandlersWired) {
      console.log('✅ ConsolidatedWebSocketManager - Message routing updated');
      return;
    }
    this.infrastructureHandlersWired = true;

    // Handle server ping — respond with pong to keep connection alive
    websocketService.on('ping', () => {
      websocketService.send('pong');
    });

    // On a genuine reconnect the server side starts a FRESH connection with no
    // farm subscription and we may have missed messages while down. Re-assert
    // the farm subscription and broadcast a resync signal so live views can
    // re-hydrate (see useResyncOnReconnect). This is the root-cause fix for
    // "live views freeze until I refresh".
    websocketService.on('ws_reconnected', () => {
      if (this.subscribedFarmId) {
        websocketService.send('subscribe_farm', { farm_id: this.subscribedFarmId });
        console.log(`📡 ConsolidatedWebSocketManager - Re-subscribed to farm ${this.subscribedFarmId} after reconnect`);
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ws:reconnected'));
      }
    });
    
    // Listen to connection status changes
    websocketService.onConnectionStatus((status) => {
      console.log('🔌 ConsolidatedWebSocketManager - Connection status changed:', status);
      this.updateConnectionState({ status });
    });
    
    console.log('✅ ConsolidatedWebSocketManager - Message routing setup complete');
  }
  
  // Update connection state
  private updateConnectionState(updates: Partial<ConnectionState>): void {
    this.connectionState = { ...this.connectionState, ...updates };
  }
  
  // Sync token before connection
  private syncTokenBeforeConnection(): void {
    // In cookie-only auth there is intentionally NO JS token — the httpOnly
    // cookie authenticates the WS handshake — so the "no token" warning would
    // be misleading noise. Only warn in dual mode where a token is expected.
    if (authService.isCookieAuth()) {
      return;
    }
    const tokenStatus = websocketService.getTokenStatus();
    console.log('🔐 ConsolidatedWebSocketManager - Token status before connection:', tokenStatus);

    if (!tokenStatus.hasToken) {
      console.warn('⚠️ ConsolidatedWebSocketManager - No authentication token available');
    } else {
      console.log('✅ ConsolidatedWebSocketManager - Authentication token ready');
    }
  }
  
  // Legacy compatibility methods
  async connectWithToken(token: string): Promise<void> {
    console.log('🔐 ConsolidatedWebSocketManager - Setting token manually and connecting');
    websocketService.setToken(token);
    return this.connect();
  }
  
  // Get current WebSocket service instance (for compatibility)
  getWebSocketService() {
    return websocketService;
  }
}

// Export singleton instance
export const consolidatedWebSocketManager = ConsolidatedWebSocketManager.getInstance();